import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { assoc, pick, prop, propOr, reduce } from 'ramda';
import { Actions, Title } from '../../components';
import QDocument from '../../_graphql/queries/documents/QDocument';
import withUser from '../../withUser';
import { verifyRouteAccess } from '../helpers/routeAccess';
import { DOCUMENT_HISTORY_FILTER_DEFAULTS } from '../../_CONST';
import DocumentHistorySearchFilters from './components/documentHistory/DocumentHistorySearchFilters';
import DocumentHistoryList from './components/documentHistory/DocumentHistoryList';

const FILTER_KEYS = ['search', 'targetType', 'period', 'sort'];
const getDefaultFilterState = () => pick(FILTER_KEYS, DOCUMENT_HISTORY_FILTER_DEFAULTS);

const normalizeFilterState = (filterState = {}) => reduce(
  (normalizedFilters, key) => assoc(
    key,
    propOr(prop(key, DOCUMENT_HISTORY_FILTER_DEFAULTS), key, filterState),
    normalizedFilters
  ),
  {},
  FILTER_KEYS
);

const DocumentHistory = ({ userTeam }) => {
  const { documentId } = useParams();
  const [filterState, setFilterState] = useState(getDefaultFilterState);
  const [filtersDisabled, setFiltersDisabled] = useState(false);
  const normalizedFilterState = useMemo(() => normalizeFilterState(filterState), [filterState]);
  const { search, targetType, period, sort } = normalizedFilterState;

  // Query (app/_graphql/Query.js) reloads the query whenever the identity of `args`
  // changes, so an inline object literal would re-run QDocumentQuery on every render
  // triggered by the filter state.
  const documentQueryArgs = useMemo(() => ({ documentId }), [documentId]);

  const handleReset = useCallback(() => setFilterState(getDefaultFilterState()), []);
  const handleFilterChange = useCallback(
    filterKey => value => setFilterState(previous => assoc(filterKey, value, previous)),
    []
  );

  return (
    <QDocument args={documentQueryArgs}>
      {data => {
        const { document = {} } = data;
        const accessDenied = verifyRouteAccess(userTeam, document, 'l\'historique du document');
        if (accessDenied) return accessDenied;
        return (
          <div className="h-[80vh] flex flex-col overflow-hidden">
            <Actions
              title={(
                <Title
                  backTo={`/offre/${documentId}/editer`}
                  title={`Historique du document : "${propOr('', 'name', document)}"`}
                />
              )}
            />
            <DocumentHistorySearchFilters
              search={search}
              targetType={targetType}
              period={period}
              sort={sort}
              onFilterChange={handleFilterChange}
              disabled={filtersDisabled}
            />
            <DocumentHistoryList
              documentId={documentId}
              filterState={normalizedFilterState}
              onResetFilters={handleReset}
              onEmptyStateChange={setFiltersDisabled}
            />
          </div>
        );
      }}
    </QDocument>
  );
};

export default withUser(DocumentHistory);
